import { type AiActionPolicyV1, AiActionPolicyV1Schema } from "@adpilot/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AiActionPolicyFields } from "@/components/ai/AiActionPolicyFields";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  fetchCampaignActionCapabilities,
  fetchCampaignActionOperations,
  fetchCampaignActionPolicy,
  type ProposalAction,
  proposalActionSchema,
  proposeCampaignAction,
  updateCampaignActionPolicy,
} from "@/lib/ai-action-operation-api";
import { getApiErrorMessage } from "@/lib/api-error";

interface CampaignAiActionPanelProps {
  campaignId: string;
  shopId: string;
  canManageAi: boolean;
}

/** Configuration saves consent only; proposals never bypass explicit enrollment or approval. */
export function CampaignAiActionPanel({ ...props }: CampaignAiActionPanelProps) {
  return <CampaignAiActionEditor key={`${props.shopId}:${props.campaignId}`} {...props} />;
}

function CampaignAiActionEditor({ campaignId, shopId, canManageAi }: CampaignAiActionPanelProps) {
  const { t } = useTranslation(["smartCreation", "aiOperations", "common"]);
  const queryClient = useQueryClient();
  const policyKey = ["campaign-action-policy", shopId, campaignId] as const;
  const operationsKey = ["campaign-ai-action-operations", shopId, campaignId] as const;
  const policyQuery = useQuery({
    queryKey: policyKey,
    queryFn: () => fetchCampaignActionPolicy(campaignId, shopId),
    refetchOnWindowFocus: false,
    retry: false,
  });
  const capabilityQuery = useQuery({
    queryKey: ["campaign-action-capabilities", shopId, campaignId],
    queryFn: () => fetchCampaignActionCapabilities(campaignId, shopId),
    staleTime: 30_000,
    retry: false,
  });
  const [page, setPage] = useState(1);
  const operationsQuery = useQuery({
    queryKey: [...operationsKey, page],
    queryFn: () => fetchCampaignActionOperations(campaignId, shopId, page),
    retry: false,
  });
  const [draft, setDraft] = useState<AiActionPolicyV1 | null>(null);
  const dirty = draft !== null;
  const candidate =
    draft ??
    policyQuery.data?.policy ??
    (policyQuery.data
      ? AiActionPolicyV1Schema.parse({
          version: 1,
          revision: 1,
          source: "campaign",
          shopId,
          campaignId,
        })
      : null);
  const [reasons, setReasons] = useState<Partial<Record<ProposalAction, string>>>({});
  const requestKeys = useRef(new Map<string, string>());

  const save = useMutation({
    mutationFn: () => {
      if (!canManageAi || !candidate || !policyQuery.data)
        throw new Error("Campaign action policy unavailable");
      return updateCampaignActionPolicy(campaignId, shopId, {
        expectedRevision: policyQuery.data.policy?.revision ?? 0,
        policy: candidate,
      });
    },
    onSuccess: (result) => {
      queryClient.setQueryData(policyKey, {
        policy: result.policy,
        executionEnabled: policyQuery.data?.executionEnabled ?? false,
      });
      setDraft(null);
      void queryClient.invalidateQueries({ queryKey: ["campaign", campaignId] });
      toast.success(t("review.aiActions.campaign.saved"));
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });
  const propose = useMutation({
    mutationFn: (action: ProposalAction) => {
      if (
        !canManageAi ||
        !policyQuery.data?.policy?.actions[action] ||
        policyQuery.data.policy.dryRun
      )
        throw new Error("Campaign action permission unavailable");
      const scopedAction = `${shopId}:${campaignId}:${action}`;
      let idempotencyKey = requestKeys.current.get(scopedAction);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        requestKeys.current.set(scopedAction, idempotencyKey);
      }
      return proposeCampaignAction(campaignId, shopId, { action, idempotencyKey });
    },
    onSuccess: (result, action) => {
      requestKeys.current.delete(`${shopId}:${campaignId}:${action}`);
      if (result.status === "unavailable") {
        setReasons((current) => ({ ...current, [action]: result.reason }));
      } else {
        setReasons((current) => ({ ...current, [action]: undefined }));
        toast.success(t("review.aiActions.campaign.proposed", { count: result.operations.length }));
        void queryClient.invalidateQueries({ queryKey: operationsKey });
      }
    },
    onError: async (error) => toast.error(await getApiErrorMessage(error)),
  });

  const capabilities = capabilityQuery.data?.capabilities;
  const actionSupported = (action: ProposalAction) => {
    const matches = capabilities?.filter((item) => item.action === action) ?? [];
    return matches.length === 1 && matches[0]?.state === "supported";
  };
  const reasonText = (reason: string) =>
    t(`review.aiActions.campaign.reasons.${reason}`, {
      defaultValue: t("review.aiActions.campaign.unavailable"),
    });
  const updateCandidate = async (next: AiActionPolicyV1): Promise<boolean> => {
    if (!canManageAi || save.isPending) return false;
    setDraft({ ...next, revision: (policyQuery.data?.policy?.revision ?? 0) + 1 });
    return true;
  };

  if (policyQuery.isPending) return <Skeleton className="h-80 w-full rounded-lg" />;
  if (policyQuery.isError || !candidate)
    return (
      <ErrorState
        message={t("review.aiActions.campaign.loadError")}
        onRetry={() => void policyQuery.refetch()}
        isRetrying={policyQuery.isFetching}
      />
    );
  return (
    <div className="flex flex-col gap-5" data-campaign-ai-action-panel>
      <p className="text-xs leading-5 text-muted-foreground">
        {t("review.aiActions.campaign.consentOnly")}
      </p>
      <AiActionPolicyFields
        policy={candidate}
        capabilities={capabilities}
        capabilityStatus={
          capabilityQuery.isPending ? "loading" : capabilityQuery.isError ? "unavailable" : "ready"
        }
        disabled={!canManageAi || save.isPending}
        onPolicyChange={updateCandidate}
      />
      {capabilityQuery.isError ? (
        <ErrorState
          variant="inline"
          message={t("review.aiActions.campaign.capabilityError")}
          onRetry={() => void capabilityQuery.refetch()}
          isRetrying={capabilityQuery.isFetching}
        />
      ) : null}
      <Field orientation="horizontal" data-disabled={!canManageAi || save.isPending || undefined}>
        <Switch
          id="campaign-ai-action-live"
          checked={!candidate.dryRun}
          disabled={!canManageAi || save.isPending}
          onCheckedChange={(checked) =>
            void updateCandidate({ ...candidate, dryRun: !checked, approvalRiskThreshold: "all" })
          }
        />
        <div className="flex flex-col gap-1">
          <FieldLabel htmlFor="campaign-ai-action-live">
            {t("review.aiActions.campaign.allowApprovedExecution")}
          </FieldLabel>
          <FieldDescription>
            {t(
              candidate.dryRun
                ? "review.aiActions.campaign.safeMode"
                : "review.aiActions.campaign.approvalMode",
            )}
          </FieldDescription>
        </div>
      </Field>
      {!policyQuery.data?.executionEnabled ? (
        <p className="text-xs text-muted-foreground">
          {t("review.aiActions.campaign.environmentDisabled")}
        </p>
      ) : null}
      <div className="flex justify-end gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={save.isPending || propose.isPending}
          loading={policyQuery.isFetching}
          title={t("review.aiActions.campaign.reloadHint")}
          onClick={async () => {
            const refreshed = await policyQuery.refetch();
            if (refreshed.isSuccess) {
              setDraft(null);
              setReasons({});
            }
          }}
        >
          {t("review.aiActions.campaign.reload")}
        </Button>
        <Button
          size="sm"
          disabled={!canManageAi || !dirty}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          {t("review.aiActions.campaign.save")}
        </Button>
      </div>
      <section className="flex flex-col gap-3 border-t border-line pt-4">
        <h3 className="text-sm font-semibold">{t("review.aiActions.campaign.proposalsTitle")}</h3>
        <p className="text-xs text-muted-foreground">
          {t("review.aiActions.campaign.proposalsHint")}
        </p>
        <div className="divide-y divide-line rounded-lg border border-line">
          {proposalActionSchema.options.map((action) => (
            <div key={action} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="flex min-w-0 flex-col gap-1">
                <span className="text-xs font-medium">{t(`review.aiActions.${action}`)}</span>
                {reasons[action] ? (
                  <p className="text-xs text-muted-foreground" role="status">
                    {reasonText(reasons[action])}
                  </p>
                ) : null}
              </div>
              <Button
                size="sm"
                variant="outline"
                aria-label={t("review.aiActions.campaign.proposeAction", {
                  action: t(`review.aiActions.${action}`),
                })}
                disabled={
                  !canManageAi ||
                  dirty ||
                  !policyQuery.data?.policy?.actions[action] ||
                  !!policyQuery.data.policy.dryRun ||
                  !actionSupported(action) ||
                  propose.isPending
                }
                loading={propose.isPending && propose.variables === action}
                onClick={() => propose.mutate(action)}
              >
                {t("review.aiActions.campaign.propose")}
              </Button>
            </div>
          ))}
        </div>
      </section>
      <section className="flex flex-col gap-3 border-t border-line pt-4">
        <h3 className="text-sm font-semibold">{t("review.aiActions.campaign.recordsTitle")}</h3>
        {operationsQuery.isPending ? (
          <Skeleton className="h-24 w-full" />
        ) : operationsQuery.isError ? (
          <ErrorState
            variant="inline"
            message={t("review.aiActions.campaign.recordsError")}
            onRetry={() => void operationsQuery.refetch()}
            isRetrying={operationsQuery.isFetching}
          />
        ) : operationsQuery.data?.data.length ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("review.aiActions.campaign.action")}</TableHead>
                <TableHead>{t("review.aiActions.campaign.status")}</TableHead>
                <TableHead>{t("review.aiActions.campaign.record")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {operationsQuery.data.data.map((operation) => (
                <TableRow key={operation.id}>
                  <TableCell>{t(`review.aiActions.${operation.proposal.action}`)}</TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {t(`aiOperations:actionOperation.status.${operation.status}`)}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="xs"
                      variant="link"
                      render={
                        <Link
                          to="/ai-operations/actions/$operationId"
                          params={{ operationId: operation.id }}
                          search={{ shopId }}
                        />
                      }
                    >
                      {t("review.aiActions.campaign.openRecord")}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("review.aiActions.campaign.noRecords")}
          </p>
        )}
        {operationsQuery.data && (page > 1 || operationsQuery.data.pagination.hasMore) ? (
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={page === 1 || operationsQuery.isFetching}
              onClick={() => setPage((current) => current - 1)}
            >
              {t("review.aiActions.campaign.previous")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!operationsQuery.data.pagination.hasMore || operationsQuery.isFetching}
              onClick={() => setPage((current) => current + 1)}
            >
              {t("review.aiActions.campaign.next")}
            </Button>
          </div>
        ) : null}
      </section>
    </div>
  );
}
